import React from 'react';
import PropTypes from 'prop-types';
import SVG from 'react-inlinesvg';
import { useTranslation } from 'react-i18next';
import { Icon } from '@patternfly/react-core';
import ArrowDownIcon from '@patternfly/react-icons/dist/esm/icons/angle-down-icon';
import ArrowUpIcon from '@patternfly/react-icons/dist/esm/icons/angle-up-icon';
import useLocalStorageState from 'hooks/useLocalStorageState';
import CollapseIcon from 'img/icons/web-icon-caret-thin-up.svg';
import ExpandIcon from 'img/icons/web-icon-caret-thin-down.svg';

import './styles.scss';

export default function ExpandableSection({
  title,
  children,
  localStorageStateKey,
  initialState,
  color,
  bare,
  iconVariant,
}) {
  const { t } = useTranslation();

  const [isExpanded, setExpanded] = useLocalStorageState(
    localStorageStateKey,
    initialState,
  );

  const stripClasses = [
    'expandable-section__clickable-strip',
    bare
      ? 'expandable-section__clickable-strip--bare'
      : `expandable-section__clickable-strip--${color}`,
  ].join(' ');

  const renderIcon = () => {
    if (iconVariant === 'checkbox') {
      const CheckboxStyleIcon = isExpanded ? ArrowUpIcon : ArrowDownIcon;
      return (
        <Icon className="expandable-section__pf-icon">
          <CheckboxStyleIcon color="#121212" />
        </Icon>
      );
    }

    return (
      <SVG src={isExpanded ? CollapseIcon : ExpandIcon}>
        {isExpanded ? t('Collapse') : t('Expand')}
      </SVG>
    );
  };

  return (
    <div className="expandable-section">
      <div
        className={stripClasses}
        role="button"
        tabIndex={0}
        onClick={() => setExpanded((prev) => !prev)}
      >
        <div>{title}</div>
        <div className="expandable-section__arrow-icon">{renderIcon()}</div>
      </div>
      {isExpanded ? children : null}
    </div>
  );
}

ExpandableSection.propTypes = {
  title: PropTypes.string.isRequired,
  children: PropTypes.node,
  localStorageStateKey: PropTypes.string,
  initialState: PropTypes.bool,
  color: PropTypes.oneOf(['dark', 'light']),
  bare: PropTypes.bool,
  iconVariant: PropTypes.oneOf(['default', 'checkbox']),
};

ExpandableSection.defaultProps = {
  initialState: true,
  children: null,
  color: 'dark',
  localStorageStateKey: undefined,
  bare: false,
  iconVariant: 'default',
};
